// @flow

import React, { useEffect } from "react";
import { localize } from "redux-i18n";
import { connect } from "react-redux";
import { compose } from "redux";

import type { State } from "@store/reducers";
import { BoxField, SwitchButton, Loader, Text, View } from "@components";
import clientActionCreators from "@modules/client/actionCreator";
import withErrorHandler from "@components/error";
import { isLoading, getAppSettings } from "@modules/client/selectors";
import type { AppConfiguration } from "@modules/client/reducer/app/types";
import {
  withPermissions,
  PermissionEnum,
  type PropsFromPermissions,
} from "@modules/account/components";

type PropsFromDispatch = $Exact<{
  getAppSettings: typeof clientActionCreators.GetAppClientSettings,
  updateAppSettings: typeof clientActionCreators.UpdateAppClientSettings,
}>;

type PropsFromState = $Exact<{
  isLoading: boolean,
  appSettings: AppConfiguration,
}>;

type Props = $Exact<{
  clientId: number | null,
  ...PropsFromLocalize,
  ...PropsFromPermissions,
  ...PropsFromDispatch,
  ...PropsFromState,
}>;

type AppData = $Exact<{
  value: boolean,
  title: string,
  description: string,
  onChange: (value: boolean) => void,
}>;

const AppSettings = (props: Props) => {
  const { appSettings, clientId } = props;

  useEffect(() => {
    props.getAppSettings({
      clientId,
    });
  }, [clientId]);

  const updateAppSettings = (value: string) => {
    return (enabled: boolean) => {
      props.updateAppSettings({ clientId, data: { ...appSettings, [value]: enabled } });
    };
  };

  const appFieldsData: AppData[] = [
    {
      value: appSettings.showNotificationContacts,
      title: props.t("SHOW_NOTIFICATION_CONTACTS"),
      description: props.t("SHOW_NOTIFICATION_CONTACTS_DESCRIPTION"),
      onChange: updateAppSettings("showNotificationContacts"),
    },
    {
      value: appSettings.showWorkRelatedWarning,
      title: props.t("SHOW_WORK_RELATED_ABSENCE_WARNING"),
      description: props.t("SHOW_WORK_RELATED_ABSENCE_WARNING_DESCRIPTION"),
      onChange: updateAppSettings("showWorkRelatedWarning"),
    },
    {
      value: appSettings.showTriggeredAlerts,
      title: props.t("SHOW_TRIGGERING_ALERTS"),
      description: props.t("SHOW_TRIGGERING_ALERTS_DESCRIPTION"),
      onChange: updateAppSettings("showTriggeredAlerts"),
    },
    {
      value: appSettings.allowDocumentUpload,
      title: props.t("ALLOW_DOCUMENT_UPLOAD"),
      description: props.t("ALLOW_DOCUMENT_UPLOAD_DESCRIPTION"),
      onChange: updateAppSettings("allowDocumentUpload"),
    },
    {
      value: appSettings.enableManagerMode,
      title: props.t("ENABLE_MANAGER_MODE"),
      description: props.t("ENABLE_MANAGER_MODE_DESCRIPTION"),
      onChange: updateAppSettings("enableManagerMode"),
    },
  ];

  return (
    <>
      {props.isLoading && <Loader />}
      <View className="gs-container" flex={1} gap="6">
        <Text type="h6">{props.t("APP_FEATURES")}</Text>

        <View gap={3}>
          {appFieldsData.map(item => (
            <BoxField key={item.title} title={item.title} description={item.description}>
              <SwitchButton active={item.value} onChange={item.onChange} />
            </BoxField>
          ))}
        </View>
      </View>
    </>
  );
};

const mapStateToProps = (state: State): PropsFromState => {
  return {
    isLoading: isLoading(state),
    appSettings: getAppSettings(state),
  };
};

const mapDispatchToProps: PropsFromDispatch = {
  getAppSettings: clientActionCreators.GetAppClientSettings,
  updateAppSettings: clientActionCreators.UpdateAppClientSettings,
};

export default compose(
  withErrorHandler({}),
  localize(),
  connect(mapStateToProps, mapDispatchToProps),
  withPermissions({ permissionId: PermissionEnum.ACCESS_ADMIN_SERVICE }),
)(AppSettings);
